"use client"

import { CommandKProvider } from "./CommandK"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { usePathname } from "next/navigation"
import { PostHogProvider } from "./providers/PostHogProvider"
import { SignupTracking } from "./providers/SignupTracking"
import { ClerkProvider } from "@clerk/nextjs"

const Providers = ({ children }: any) => (
  <ClerkProvider>
    <SignupTracking />
    <ContentVisibilityProvider>
      <PostHogProvider>
        <CommandKProvider>{children}</CommandKProvider>
      </PostHogProvider>
    </ContentVisibilityProvider>
  </ClerkProvider>
)

export default Providers

const initialContentVisibilityContext = {
  isVisible: false,
  setVisibility: () => {},
  hide: (link: string) => {},
}

const ContentVisibilityContext = createContext(initialContentVisibilityContext)

export const useContentVisibility = () => useContext(ContentVisibilityContext)

export const ContentVisibilityProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [isVisible, setIsVisible] = useState(true)
  const pathname = usePathname()

  const hide = useCallback(
    (link: string) => {
      if (pathname === link || pathname === link.split("#")[0]) return

      setIsVisible(false)
    },
    [pathname]
  )

  const commandKContextValue = useMemo(
    () => ({ isVisible, setIsVisible, hide, setVisibility: () => {} }),
    [isVisible, setIsVisible, hide]
  )

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        setIsVisible(true);
        return;
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (isVisible) return

      setIsVisible(true)
    }, 5000)
  }, [isVisible])

  useEffect(() => {
    setIsVisible(true)
  }, [pathname])

  return (
    <ContentVisibilityContext.Provider value={commandKContextValue}>
      {children}
    </ContentVisibilityContext.Provider>
  )
}